import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Mail, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import { requestMagicLink } from '../services/api';

/**
 * EmailGate - A calm sign-in gate for protected routes
 * Shows email-only magic link authentication inline
 * OHMNAI aesthetic: quiet, no pressure, no urgency
 */
const EmailGate = ({ next = '/dashboard' }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState(null);
  const [showResend, setShowResend] = useState(false);

  // Store the intended destination for post-login redirect
  useEffect(() => {
    if (next && next !== '/') {
      localStorage.setItem('ohmnai_auth_redirect', next);
    }
  }, [next]);

  // Show resend after 20 seconds
  useEffect(() => {
    let timer;
    if (isSent) {
      timer = setTimeout(() => setShowResend(true), 20000);
    }
    return () => clearTimeout(timer);
  }, [isSent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await requestMagicLink(email);
      setIsSent(true);
      setShowResend(false);
    } catch (err) {
      console.error('Magic link request error:', err);
      setError(err.message || 'Failed to send sign-in link');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setShowResend(false);
    setIsLoading(true);
    
    try {
      await requestMagicLink(email);
      setShowResend(false);
      // Reset the timer
      setTimeout(() => setShowResend(true), 20000);
    } catch (err) {
      setError(err.message || 'Failed to resend link');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-mist via-mist-light to-fern">
      {/* Header */}
      <header className="border-b border-calm-border bg-fern-card/80 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <div 
            className="text-2xl font-bold text-calm-text cursor-pointer" 
            style={{fontFamily: 'DM Serif Display, serif'}}
            onClick={() => navigate('/')}
          >
            OHMNAI
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-calm-text-secondary hover:text-forest hover:bg-fern"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to home
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-md mx-auto px-6 py-16 sm:py-24">
        <div className="bg-fern-card rounded-2xl p-8 sm:p-10 border border-calm-border shadow-lg">
          {!isSent ? (
            <>
              {/* Sign-in form */}
              <div className="text-center mb-8">
                <div className="w-14 h-14 bg-herbal-light rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-7 w-7 text-forest" />
                </div>
                <h1 className="text-2xl text-calm-text mb-2" style={{fontFamily: 'DM Serif Display, serif'}}>
                  Sign in with email
                </h1>
                <p className="text-calm-text-secondary text-sm">
                  We'll send a quiet link. No passwords.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full py-5 px-4 text-base bg-fern-light border-calm-border focus:border-forest focus:ring-forest/20"
                    disabled={isLoading}
                    autoFocus
                  />
                </div>

                {error && (
                  <div className="bg-red-50 rounded-xl p-3 border border-red-200">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isLoading || !email}
                  className="w-full bg-forest hover:bg-forest-dark text-white py-5 text-base rounded-xl transition-colors"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Sending link...
                    </>
                  ) : (
                    'Send link'
                  )}
                </Button>
              </form>

              <p className="text-xs text-calm-text-secondary text-center mt-6">
                No urgency. No passwords.
              </p>
            </>
          ) : (
            /* Email sent confirmation */
            <div className="text-center space-y-5 fade-in-up">
              <div className="w-16 h-16 bg-herbal-light rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-10 w-10 text-forest" />
              </div>
              <h1 className="text-2xl text-calm-text" style={{fontFamily: 'DM Serif Display, serif'}}>
                Check your email
              </h1>
              <p className="text-calm-text-secondary text-sm">
                We sent a sign-in link to<br />
                <span className="text-forest font-medium">{email}</span>
              </p>
              
              <p className="text-xs text-calm-text-secondary">
                If you don't see it, check spam.
              </p>

              <div className="flex flex-col gap-3 pt-2">
                {showResend && (
                  <Button
                    onClick={handleResend}
                    disabled={isLoading}
                    variant="outline"
                    className="border-calm-border text-forest hover:bg-fern text-sm"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : null}
                    Resend link
                  </Button>
                )}
                
                <Button
                  onClick={() => {
                    setIsSent(false);
                    setEmail('');
                    setShowResend(false);
                  }}
                  variant="ghost"
                  className="text-calm-text-secondary hover:text-forest text-sm"
                >
                  Use a different email
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailGate;
